'use client';

import { type ComponentProps, type ReactNode } from 'react';
import { Button, type ButtonProps, Text } from '@bridebook/aura';
import { RoundedModalHeader, RoundedModalTitle } from '../rounded-modal/rounded-modal-header';
import { RoundedModalHeaderIllustration } from '../rounded-modal/rounded-modal-header-illustration';
import { DrawerHeader, DrawerTitle } from '../../drawer/drawer';
import { useDrawerOrModalContext } from '../drawer-or-modal/drawer-or-modal-context';
import { DrawerOrModalContent, DrawerOrModalFooter } from '../drawer-or-modal/drawer-or-modal';

export type ConfirmationDrawerOrModalContentProps = {
  title: string;
  text?: string | ReactNode;
  /**
   * Optional illustration above the title (desktop) or at the top of content (mobile).
   * When set (including `null`), replaces the illustration implied by `variant`.
   * When omitted, the variant default is used.
   */
  headerIllustration?: ReactNode;
  primaryButtonProps?: ComponentProps<typeof Button>;
  secondaryButtonProps?: ComponentProps<typeof Button>;
  variant?: 'primary' | 'destructive' | 'destructive-no-image';
  headerTitleVariant?: ComponentProps<typeof RoundedModalTitle>['variant'];
};

type VariantToButtonVariant = {
  first: ButtonProps['variant'];
  second: ButtonProps['variant'];
  illustration?: ReactNode;
};

const variantToButtonVariant: Record<
  NonNullable<ConfirmationDrawerOrModalContentProps['variant']>,
  VariantToButtonVariant
> = {
  primary: { first: 'primary', second: 'secondary' },
  destructive: {
    first: 'destructive',
    second: 'tertiary',
    illustration: <RoundedModalHeaderIllustration variant="alert" />,
  },
  'destructive-no-image': { first: 'destructive', second: 'tertiary', illustration: null },
};

/**
 * Responsive confirmation content that renders using modal primitives on desktop
 * and drawer primitives on mobile. Must be rendered inside a DrawerOrModal.
 *
 * Reuses DrawerOrModalContent and DrawerOrModalFooter for responsive switching.
 * The header is rendered manually because illustration placement differs between
 * desktop (inside header) and mobile (inside content area).
 */
export const ConfirmationDrawerOrModalContent = ({
  title,
  text,
  headerIllustration,
  primaryButtonProps,
  secondaryButtonProps,
  variant = 'primary',
  headerTitleVariant = 'coupleside',
}: ConfirmationDrawerOrModalContentProps) => {
  const { isAboveBreakpoint, showCloseButton } = useDrawerOrModalContext();

  const illustrationFromVariant = variantToButtonVariant[variant].illustration;
  const illustrationNode =
    headerIllustration !== undefined ? headerIllustration : illustrationFromVariant;

  const textNode = text && (
    <div className="text-center">
      {typeof text === 'string' ? (
        <Text variant="typo-base" className="text-center text-space">
          {text}
        </Text>
      ) : (
        text
      )}
    </div>
  );

  return (
    <>
      {/* Header — illustration placement differs: desktop=inside header, mobile=inside content */}
      {isAboveBreakpoint ? (
        <RoundedModalHeader showCloseButton={showCloseButton}>
          {illustrationNode}
          <RoundedModalTitle variant={headerTitleVariant}>{title}</RoundedModalTitle>
        </RoundedModalHeader>
      ) : (
        <DrawerHeader className="flex flex-col gap-4">
          <div className="flex justify-center">{illustrationNode}</div>
          <DrawerTitle size="lg">{title}</DrawerTitle>
        </DrawerHeader>
      )}

      {!!text && <DrawerOrModalContent>{textNode}</DrawerOrModalContent>}

      <DrawerOrModalFooter>
        {primaryButtonProps && (
          <Button
            variant={variantToButtonVariant[variant].first}
            className={isAboveBreakpoint ? 'min-w-[140px]' : 'w-full'}
            {...primaryButtonProps}
          />
        )}
        {secondaryButtonProps && (
          <Button
            variant={variantToButtonVariant[variant].second}
            className={isAboveBreakpoint ? 'min-w-[140px]' : 'w-full'}
            {...secondaryButtonProps}
          />
        )}
      </DrawerOrModalFooter>
    </>
  );
};
